import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import {
  ComparisonTablePDF,
  TComparisonTablePDFProps,
} from './ComparisonTablePDF/ComparisonTablePDF';
import { FeatureHighlight } from './FeatureHighlightPDF/FeatureHighlightPDF';
import { IwocaLogoPDF } from './IwocaLogoPDF/IwocaLogoPDF';

type TProps = {
  flexiLoanTableData: TComparisonTablePDFProps & {
    preferredCommissionRateMessage: string | null;
  };
};

export const ProductsPagePDF = ({ flexiLoanTableData }: TProps) => {
  const styles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Helvetica',
      fontSize: 10,
      color: 'rgba(12, 36, 64, 1)',
      lineHeight: 1.2,
    },
    header: {
      fontSize: 14,
      marginBottom: 8,
      textAlign: 'center',
      color: 'rgba(251,83,74,1)',
    },
    section: { marginBottom: 24 },
    howItWorksSection: {
      flexDirection: 'row',
      marginTop: 10,
      justifyContent: 'space-between',
    },
    preferredCommissionRateMessage: { marginTop: 14 },
    step: { width: '22%' },
    stepTitle: { fontFamily: 'Helvetica-Bold', fontSize: 12, marginBottom: 8 },
  });

  return (
    <Document>
      <Page style={styles.page}>
        <IwocaLogoPDF />
        {/* Flexi-Loan Features */}
        <View style={styles.section}>
          <Text style={styles.header}>Flexi-Loan Features</Text>

          <FeatureHighlight
            items={[
              'Loans up to £1 million',
              'Unsecured',
              'Overpay or settle at any time with no additional cost',
            ]}
          />

          <ComparisonTablePDF {...flexiLoanTableData} />

          {flexiLoanTableData.preferredCommissionRateMessage && (
            <Text style={styles.preferredCommissionRateMessage}>
              {flexiLoanTableData.preferredCommissionRateMessage}
            </Text>
          )}
        </View>

        {/* Document Requirements Section */}
        <View style={styles.section}>
          <Text style={styles.header}>Document Requirements</Text>
          <Text style={{ textAlign: 'center', marginBottom: 10 }}>
            Instant decision available up to £100K
          </Text>

          <ComparisonTablePDF
            data={[
              {
                range: '£0 - £25,000',
                bankStatements: 'Not required',
                filedCompanyAccounts: 'Not required',
              },
              {
                range: '£25,001 - £75,000',
                bankStatements: '6 months',
                filedCompanyAccounts: 'Not required',
              },
              {
                range: '£75,001 - £150,000',
                bankStatements: '6 months',
                filedCompanyAccounts: 'Required',
              },
              {
                range: '£150,001 - £1,000,000',
                bankStatements: '6 months',
                filedCompanyAccounts: 'Required',
              },
            ]}
            labels={{
              range: 'Requirements',
              bankStatements: 'Bank statements',
              filedCompanyAccounts: 'Filed company accounts',
            }}
          />
        </View>

        {/* Eligibility Section */}
        <View style={styles.section}>
          <Text style={styles.header}>
            Businesses eligible for a Flexi-Loan
          </Text>
          <FeatureHighlight
            items={[
              'UK Registered Companies',
              'Limited Companies and LLPs (no sole traders)',
              '12 months trading history',
              'Homeowners and non-homeowners',
            ]}
          />
        </View>

        {/* How It Works Section */}
        <View style={styles.section}>
          <Text style={styles.header}>How does it work?</Text>
          <View style={styles.howItWorksSection}>
            <View style={styles.step}>
              <Text style={styles.stepTitle}>1. Apply</Text>
              <Text>
                Complete your client's application in just 5 minutes through our
                introducer portal and upload their documents where required.
              </Text>
            </View>
            <View style={styles.step}>
              <Text style={styles.stepTitle}>2. Feedback in 24 hours</Text>
              <Text>
                You’ll get feedback on all your applications or receive an offer
                breakdown for approved customers.
              </Text>
            </View>
            <View style={styles.step}>
              <Text style={styles.stepTitle}>3. Drawdown</Text>
              <Text>
                Confirm your client wants to take the offer and their dedicated
                account manager will help them through the drawdown process.
              </Text>
            </View>
            <View style={styles.step}>
              <Text style={styles.stepTitle}>4. Earn commission</Text>
              <Text>
                Once we’ve funded your client, we’ll then pay you any commission
                due.
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
